import type {LinkTile} from "../domain/entities/link-tile.entity";
import type {RteValue} from "../domain/rte-value.type";
import {LinkTarget} from "../domain/entities/link-targets/link-target";
import {ExternalLinkTarget} from "../domain/entities/link-targets/external-link-target";
import {ShortIdHelper} from "./short-id.helper";
import {RteValueHelper} from "./rte-value.helper";

/**
 * Every user-authored field of a tile, as edited in `ha-dialog-link-tile
 * -edit` — everything `LinkTile` has except its Gridstack position/size
 * (x/y/w/h, decided by the caller placing the widget) and its `id` (decided
 * once, at creation, never edited).
 */
export interface LinkTileDraft {
    readonly text: RteValue;
    readonly target: LinkTarget | null;
    /**
     * PREVIEW ONLY, and never persisted. A picked file is read into a data URL
     * so the tile can show it immediately, before anything has been uploaded.
     * The moment the upload has happened, `imageAssetUid` is what the tile
     * renders from.
     */
    readonly imageDataUrl: string | null;
    /**
     * The file the user just picked, not yet uploaded. Uploading happens at
     * SAVE, not at pick: the asset store has no delete path and no reaper, so
     * a file uploaded for a dialog the user then cancels is litter nobody can
     * clean up.
     */
    readonly imageFile: File | null;
    /** The asset-store reference of an image that IS uploaded — a ShortId. */
    readonly imageAssetUid: string | null;
    readonly color: string | null;
    readonly icon: string | null;
}

/**
 * Pure helpers for the Linkview dashboard-creator (Stefan, 2026-09-03/04).
 * Kept dependency-free (no Gridstack/DOM imports) so they're unit testable
 * in plain Node — mirrors route-filters.ts's "pure logic in its own module"
 * rationale.
 */
export class LinkTileHelper {

    /** Default footprint (grid-cell units) for a freshly added tile. */
    public static readonly DEFAULT_WIDTH = 3;
    public static readonly DEFAULT_HEIGHT = 2;

    /**
     * The size Gridstack MEANS when its `save()` leaves `w` or `h` out and the
     * node carries no minimum.
     *
     * Gridstack strips values it considers defaults before handing a layout
     * back (`Utils.removeInternalForSave`, Gridstack 13.2.0: `if (n.w === 1 ||
     * n.w === n.minW) delete n.w;`, same for `h`, "will be re-created on
     * read"). A missing `w`/`h` is therefore the node's minimum or else 1 —
     * Gridstack's own defaults, not {@link DEFAULT_WIDTH}/{@link DEFAULT_HEIGHT},
     * which are ours and only apply to a NEW tile. Reading the gap with our
     * default turned every tile that was 1 cell wide or high into 3×2 on save
     * (Anke, 2026-09-25: a tile shrunk to 1×1 came back as 3×2 after
     * "Speichern" → "Bearbeiten").
     */
    private static readonly GRIDSTACK_OMITTED_SIZE = 1;

    /**
     * Width and height of one node from Gridstack's `save()` output, with an
     * omitted value read exactly the way Gridstack itself reads it back on load
     * (`gridstack.js`: `n.w = n.w || n.minW || 1`). The minimum matters because
     * `save()` drops a size equal to `minW`/`minH` too while keeping the
     * minimum itself on the node — today the dashboard sets none, but a later
     * minimum must not silently save as 1.
     *
     * Gridstack only ever hands back positive integer sizes, so 0 or a
     * negative value is not handled here.
     *
     * Takes the structural shape rather than Gridstack's own type so this
     * module stays free of Gridstack imports.
     */
    public static sizeFromSavedNode(
        node: {w?: number; h?: number; minW?: number; minH?: number},
    ): {w: number; h: number} {
        return {
            w: node.w ?? node.minW ?? LinkTileHelper.GRIDSTACK_OMITTED_SIZE,
            h: node.h ?? node.minH ?? LinkTileHelper.GRIDSTACK_OMITTED_SIZE,
        };
    }

    /**
     * Schemes a tile may link to. Everything else is refused — most of all
     * `javascript:` and `data:`, which EXECUTE rather than navigate.
     *
     * This is a positive list on purpose: the set of dangerous schemes is
     * open-ended (`vbscript:`, `blob:`, whatever a future engine adds), the
     * set of useful ones is four.
     */
    private static readonly ALLOWED_SCHEMES = ['http', 'https', 'mailto', 'tel'];

    /**
     * Adds a scheme if the user typed a bare domain ("example.com") — browsers
     * treat a schemeless href as relative, which would silently turn a link
     * tile into a broken same-site navigation. Leaves an already-absolute URL
     * untouched IF its scheme is one a tile may use, and refuses it otherwise.
     *
     * The refusal matters because this value goes straight into an anchor's
     * `href` (`link-tile-external.component.ts`), and this is the only place
     * that sees the raw input before it is stored. `target="_blank"` and
     * `rel="noopener noreferrer"` are no protection: they govern the new
     * browsing context, and `javascript:` never opens one — it runs.
     *
     * Today a tile lives only in the author's own localStorage, so this could
     * at worst be done to oneself. That is not why it is guarded: the tiles
     * are getting a backend, and the day they are served from the server the
     * very same string becomes stored XSS against everyone who opens the
     * board. The guard belongs in before that, not after.
     *
     * A refused URL comes back as '' rather than throwing, because
     * createTile/updateTile spread it conditionally — the tile then simply
     * has no target, a state the component already renders (a `link-tile
     * -plain` in place of `link-tile-external`).
     */
    public static normalizeUrl(rawUrl: string): string {
        const trimmed = rawUrl.trim();
        if (trimmed === '') {
            return trimmed;
        }

        // Anchored on the scheme separator, so a bare domain that merely begins with the
        // letters of a scheme ("javascript.info", "data-example.com") is not mistaken for one.
        const scheme = trimmed.match(/^([a-z][a-z0-9+.-]*):/i)?.[1];
        if (scheme === undefined) {
            return `https://${trimmed}`;
        }

        return LinkTileHelper.ALLOWED_SCHEMES.includes(scheme.toLowerCase()) ? trimmed : '';
    }

    /**
     * Only `text` is required (Anke, 2026-09-04) — everything else is
     * optional. Delegates to `RteValueHelper.hasContent`, which handles
     * either `RteValue` shape and already excludes an editor nobody actually
     * typed into (a blank Quill Delta is never truly empty —
     * `ops: [{insert: '\n'}]` — and neither is a `RichTextDocument` whose
     * only content is an empty paragraph).
     */
    public static isValidDraft(text: RteValue | null | undefined): boolean {
        return RteValueHelper.hasContent(text);
    }

    /**
     * The tile's label as plain text, formatting stripped — for the places
     * that name a tile in a NON-rendered context (an error/confirm message,
     * a log line). Delegates to `RteValueHelper.plainText` (review 0030-01,
     * MAJOR: `dashboard-creator.component.ts`'s upload failure notice used
     * to interpolate the raw Delta object here, showing literally
     * "[object Object]" instead of the tile's name — this is the fix that
     * regression drove, now generalized to either `RteValue` shape).
     */
    public static plainText(text: RteValue | null | undefined): string {
        return RteValueHelper.plainText(text);
    }

    /**
     * Migrates ANY raw stored `text` value into an `RteValue` — the ONE
     * shape-detection implementation shared by both storage layers' own
     * migration wrappers (`link-tile-mapper.ts`'s `parseText()` for the
     * backend text column, `local-storage-linkview.repository.ts`'s
     * `migrateText()` for localStorage), extracted here after review 0030-01
     * (INFO) flagged the two as near-identical, independently-maintained
     * copies.
     *
     * `raw` covers every shape `text` has ever been stored as, across both
     * layers and both RTE generations:
     * - a JSON-encoded string (`toPayload()`'s wire format for the backend
     *   column, which never decodes it itself — the client owns
     *   losslessness, same stance the sibling `target` column already
     *   takes) — parsed first;
     * - a plain object already shaped `{ops: [...]}` or `{type: 'doc', ...}`
     *   (localStorage decodes its OWN JSON blob as a whole, so a modern
     *   tile's `text` arrives here already an object, never a nested
     *   string) — used as-is, EITHER shape;
     * - a bare legacy label string (pre-2026-09-07, the era before this
     *   field held rich text at all, on EITHER layer) — becomes a single
     *   Delta insert run, with `legacyTextColor` (if any — the retired,
     *   no-longer-written separate colour field) folded in as that run's
     *   `color` attribute, so an already-chosen colour is not silently
     *   dropped the one time this runs. It stays a Delta rather than
     *   becoming a `RichTextDocument` directly: `ha-rte` already normalizes
     *   any Delta-shaped value on render (`RteValueHelper.toRichTextDocument`),
     *   so there is no need for a second conversion path here.
     */
    public static migrateText(raw: unknown, legacyTextColor?: string | null): RteValue {
        const parsed = typeof raw === 'string' ? LinkTileHelper._tryParseJson(raw) : raw;

        if (parsed && typeof parsed === 'object') {
            if (RteValueHelper.isRichTextDocument(parsed)) {
                return parsed;
            }
            if (RteValueHelper.isQuillDelta(parsed)) {
                return parsed as RteValue;
            }
        }

        const label = typeof raw === 'string' ? raw : '';
        return {
            ops: [{insert: label, ...(legacyTextColor ? {attributes: {color: legacyTextColor}} : {})}],
        } as RteValue;
    }

    private static _tryParseJson(raw: string): unknown {
        try {
            return JSON.parse(raw);
        } catch {
            return undefined;
        }
    }

    /**
     * Re-normalizes an `ExternalLinkTarget`'s URL through `normalizeUrl()` (the
     * single point of URL normalization/scheme-guarding, see its own doc
     * comment) and drops it entirely if that refuses it (empty result). Every
     * other `LinkTarget` kind passes through untouched — there is nothing to
     * normalize about a `KnowledgeObjectLinkTarget`'s uid.
     */
    private static normalizeTarget(target: LinkTarget | null): LinkTarget | undefined {
        if (!target) {
            return undefined;
        }
        if (!(target instanceof ExternalLinkTarget)) {
            return target;
        }
        const normalized = LinkTileHelper.normalizeUrl(target.url);
        return normalized ? new ExternalLinkTarget(normalized) : undefined;
    }

    /**
     * Builds a fresh tile at grid position (0,0) — autoPosition is left to
     * Gridstack itself (`addWidget({..., autoPosition: true})`), this only
     * carries the fallback footprint/id/content for the widget definition.
     * Every optional field is conditionally spread — never assigned
     * `undefined` outright — so an unset field stays truly absent rather
     * than persisting as an explicit `undefined` in the stored JSON.
     */
    public static createTile(draft: LinkTileDraft): LinkTile {
        const target = LinkTileHelper.normalizeTarget(draft.target);
        return {
            // The one mint site for a tile id, and it mints the TRANSPORT
            // spelling: a 22-character ShortId, as every other identifier in
            // this application carries outside the database. A raw uuid here
            // would travel to a backend that expects the short form.
            id: ShortIdHelper.generate(),
            text: draft.text,
            ...(target ? {target} : {}),
            ...(draft.imageAssetUid ? {imageAssetUid: draft.imageAssetUid} : {}),
            ...(draft.color ? {color: draft.color} : {}),
            ...(draft.icon ? {icon: draft.icon} : {}),
            x: 0,
            y: 0,
            w: LinkTileHelper.DEFAULT_WIDTH,
            h: LinkTileHelper.DEFAULT_HEIGHT,
        };
    }

    /**
     * Applies an edited draft onto an EXISTING tile — keeps its id/x/y/w/h
     * (Gridstack's territory, untouched by the properties dialog), replaces
     * every user-authored field wholesale (an explicitly cleared field, e.g.
     * a removed image, must actually disappear — so this is NOT a
     * conditional-merge like `createTile`, every field is set from the draft
     * as-is, present or not).
     */
    public static applyDraft(tile: LinkTile, draft: LinkTileDraft): LinkTile {
        const target = LinkTileHelper.normalizeTarget(draft.target);
        return {
            id: tile.id,
            text: draft.text,
            ...(target ? {target} : {}),
            ...(draft.imageAssetUid ? {imageAssetUid: draft.imageAssetUid} : {}),
            ...(draft.color ? {color: draft.color} : {}),
            ...(draft.icon ? {icon: draft.icon} : {}),
            x: tile.x,
            y: tile.y,
            w: tile.w,
            h: tile.h,
        };
    }

}
